import { styled } from '@mui/material';
import Button from '@mui/material/Button';

const colors = {
  primary: '#2C62C7',
  secondary: '#04682E',
  background: '#F8F8F8',
  textPrimary: '#212529',
  textSecondary: '#6C757D',
  border: '#D9D9D9'
};

export const Styles = {
  PageContainer: styled('div')({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f0f0f0'
  }),

  ContentContainer: styled('div')({
    display: 'flex',
    flex: 1
  }),

  MainContent: styled('div')({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  }),

  PageContent: styled('div')(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(1),
    minHeight: 'calc(100vh - 64px)',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2)
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3)
    }
  })),

  SectionFilters: styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    gap: '16px'
  }),

  FlexContainer: styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  }),

  SearchContainer: styled('div')({
    flex: 1,
    maxWidth: '400px'
  }),

  SearchInput: styled('input')({
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: colors.background,
    transition: 'border-color 0.3s ease',

    '&:focus': {
      outline: 'none',
      borderColor: colors.primary,
      boxShadow: `0 0 0 2px ${colors.primary}20`
    }
  }),

  SelectContainer: styled('div')({
    select: {
      padding: '8px 12px',
      fontSize: '14px',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      backgroundColor: colors.background,
      transition: 'border-color 0.3s ease',

      '&:focus': {
        outline: 'none',
        borderColor: colors.primary
      }
    }
  }),

  RectangleBar: styled('div')(({ theme }) => ({
    height: '4px',
    backgroundColor: colors.border,
    margin: theme.spacing(2, 0),
    borderRadius: '2px',
    width: '100%'
  })),

  TextBase: styled('div')({
    color: colors.textPrimary
  }),

  ShowText: styled('div')({
    fontSize: '14px',
    fontWeight: 500,
    color: colors.textSecondary
  }),

  RecordsText: styled('div')({
    fontSize: '14px',
    fontWeight: 500,
    color: colors.textSecondary
  }),

  UserCard: styled('div')({
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: colors.background,
    borderRadius: '12px',
    marginBottom: '16px',
    transition: 'transform 0.2s ease',

    '&:hover': {
      transform: 'translateY(-2px)'
    }
  }),

  UserIcon: styled('div')({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: colors.border,
    marginRight: '16px',
    flexShrink: 0
  }),

  UserInfo: styled('div')({
    flex: 1,
    minWidth: 0
  }),

  UserName: styled('div')({
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '4px'
  }),

  UserContact: styled('div')({
    fontSize: '14px',
    color: colors.textSecondary
  }),

  Buttons: styled('div')({
    display: 'flex',
    gap: '8px',
    marginLeft: 'auto',
    paddingLeft: '16px',

    '& button': {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
      transition: 'all 0.2s ease',

      '&:first-of-type': {
        backgroundColor: colors.primary,
        color: '#FFFFFF',

        '&:hover': {
          backgroundColor: '#1E4A8E'
        }
      },

      '&:last-of-type': {
        backgroundColor: colors.textPrimary,
        color: '#FFFFFF',

        '&:hover': {
          backgroundColor: colors.textSecondary
        }
      }
    }
  }),

  Pagination: styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '24px',

    button: {
      padding: '8px 12px',
      minWidth: '40px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      backgroundColor: '#FFFFFF',
      color: colors.textPrimary,
      transition: 'all 0.2s ease',

      '&.active': {
        backgroundColor: colors.textPrimary,
        color: '#FFFFFF',
        borderColor: colors.textPrimary
      },

      '&:disabled': {
        opacity: 0.6,
        cursor: 'not-allowed'
      },

      '&:hover:not(:disabled)': {
        backgroundColor: colors.background
      }
    }
  }),

  DetailContainer: styled('div')(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: colors.background,
    borderRadius: '12px',
    marginBottom: '24px',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3)
    },

    '& h2': {
      fontSize: '16px',
      fontWeight: 600,
      color: colors.primary,
      margin: '12px 0 8px',
      [theme.breakpoints.up('md')]: {
        fontSize: '18px',
        margin: '16px 0 12px'
      }
    },
    '& p': {
      fontSize: '14px',
      lineHeight: 1.5,
      margin: '6px 0',
      [theme.breakpoints.up('md')]: {
        margin: '8px 0'
      }
    }
  })),

  ButtonContainer: styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px'
  }),

  BackButton: styled(Button)({
    backgroundColor: colors.textPrimary,
    color: '#FFFFFF',
    padding: '10px 24px',
    borderRadius: '8px',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'background-color 0.2s ease',

    '&:hover': {
      backgroundColor: colors.textSecondary
    }
  })
};
